/**
 * 入口：Element Plus + pinia + router + 路由守卫。
 *
 * 说明：这里**不再挂载任何 mock 适配器**（2026-09 整条清除）。
 * 此前开发态会默认把 axios 换成本地假数据源，接口不可用时页面安静地显示演示数据 ——
 * 运营看到的假数字与真数字没有任何区别，这是本项目最危险的一类设计，已彻底移除：
 * 接口坏了就报错，页面展示错误/空态。
 *
 * ⚠️ 本文件刻意不写出被清除的那些标识名（否则「全仓 grep 必须为空」的检查会永远红）。
 *    要了解这条装配链长什么样，见 `docs/清除mock数据执行方案.md`。
 *    防复活由 `__tests__/build-artifact-no-mock.spec.ts` 与 `deploy/preflight.sh` 守。
 */
import { createApp } from 'vue'
import ElementPlus from 'element-plus'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import 'element-plus/dist/index.css'

import App from '@/App.vue'
import router from '@/router'
import { createAuthGuard } from '@/router/guard'
import { createPinia } from 'pinia'
import { useAuthStore } from '@/store/auth'
import { setLoginRedirect } from '@/api/request'
import { LOGIN_PATH } from '@/api/request'
import '@/styles/index.css'
// 移动端响应式规则：**全项目唯一一份媒体查询集中处**，必须排在
// index.css 之后（前者含打印规则，后者只含窄屏 max-width 规则，互不干扰）。
// 桌面（>=1024px）规则不在这个文件里，因此宽屏样式不受其影响。
import '@/styles/responsive.css'

async function bootstrap(): Promise<void> {
  const app = createApp(App)
  const pinia = createPinia()

  app.use(pinia)
  app.use(ElementPlus, { locale: zhCn })

  // 需要 pinia 已经激活才能取 store
  const auth = useAuthStore(pinia)

  // request 层 401 → 交给 vue-router 跳登录（带上回跳地址）
  setLoginRedirect(() => {
    const current = router.currentRoute.value
    if (current.path === LOGIN_PATH) return
    void router.replace({ path: LOGIN_PATH, query: { redirect: current.fullPath } })
  })

  router.beforeEach(createAuthGuard({ auth }))

  app.use(router)
  app.mount('#app')
}

void bootstrap()
